import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { colors, typography } from '../../../styles/theme';
import { FILTER_ACCENT } from './FilterPanel.styles';
import { OFFICIAL_UNIT_FILTER_STATUS } from '../../hooks/useOfficialUnitMapFilter';
import { formatOfficialUnitFilterCounts } from '../../utils/lotFunctionalUnits';
import { formatOfficialUnitZoomHint } from '../../utils/officialUnitFilterRequest';

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: '20px',
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  lineHeight: 1.4,
};

const retryButtonStyle = {
  border: 'none',
  background: 'none',
  padding: 0,
  color: FILTER_ACCENT,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * Estado del recorte por UF catastrales bajo su grupo de filtros.
 * El mapa pinta parcelas, así que el recuento es la única señal de cuántas
 * unidades hay detrás de cada polígono.
 *
 * @param {object} props
 * @param {string} props.status - Ver `OFFICIAL_UNIT_FILTER_STATUS`.
 * @param {number|null} [props.matchCount] - Parcelas que cumplen.
 * @param {number|null} [props.unitCount] - UF que cumplieron.
 * @param {function} [props.onRetry]
 * @param {boolean} [props.zoomLocked] - Zoom bajo el mínimo: avisar aunque aún no haya criterios.
 */
function OfficialUnitFilterStatus({
  status,
  matchCount = null,
  unitCount = null,
  onRetry = null,
  zoomLocked = false,
}) {
  const countsLabel = formatOfficialUnitFilterCounts({ unitCount, matchCount });
  const zoomHint = formatOfficialUnitZoomHint();

  const message = () => {
    if (
      zoomLocked
      && status !== OFFICIAL_UNIT_FILTER_STATUS.loading
      && status !== OFFICIAL_UNIT_FILTER_STATUS.error
      && status !== OFFICIAL_UNIT_FILTER_STATUS.ready
    ) {
      return (
        <div style={{ ...rowStyle, color: colors.textSecondary }}>
          {zoomHint}
        </div>
      );
    }

    switch (status) {
      case OFFICIAL_UNIT_FILTER_STATUS.loading:
        return (
          <div style={{ ...rowStyle, color: colors.textSecondary }}>
            <CircularProgress size={14} thickness={5} sx={{ color: FILTER_ACCENT }} />
            <span>{countsLabel ? `Actualizando · ${countsLabel}` : 'Buscando unidades…'}</span>
          </div>
        );
      case OFFICIAL_UNIT_FILTER_STATUS.ready:
        return countsLabel
          ? <div style={{ ...rowStyle, color: colors.textPanelDescription }}>{countsLabel}</div>
          : null;
      case OFFICIAL_UNIT_FILTER_STATUS.empty:
        return (
          <div style={{ ...rowStyle, color: colors.textSecondary }}>
            Ninguna unidad cumple en esta vista.
          </div>
        );
      case OFFICIAL_UNIT_FILTER_STATUS.zoom:
        return (
          <div style={{ ...rowStyle, color: colors.textSecondary }}>
            {zoomHint}
          </div>
        );
      case OFFICIAL_UNIT_FILTER_STATUS.truncated:
        return (
          <div style={{ ...rowStyle, color: colors.textSecondary }}>
            Demasiados lotes en esta vista. Acercá el zoom.
          </div>
        );
      case OFFICIAL_UNIT_FILTER_STATUS.error:
        return (
          <div style={{ ...rowStyle, color: colors.error }}>
            <span>No se pudo filtrar por unidades.</span>
            {onRetry && (
              <button type="button" style={retryButtonStyle} onClick={onRetry}>
                Reintentar
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const content = message();

  return (
    <div aria-live="polite">
      {content}
    </div>
  );
}

export default OfficialUnitFilterStatus;
